import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded'
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded'
import {
  downloadCityCsv,
  enrichCityListings,
  fetchAirbnbLogs,
  fetchAirbnbStatus,
  fetchCityListingDetail,
  fetchCityListings,
  fetchScrapeCities,
  fetchScrapeCity,
  startAirbnbScrape,
  stopAirbnbScrape,
} from '../services/adminApi'
import { PageShell, StatCard, TerminalLogs } from '../components/shared'
import ListingDetailDrawer from '../components/ListingDetailDrawer'
import { useAdminJob } from '../AdminConsole'

const GOA_EXAMPLE =
  'https://www.airbnb.co.in/s/Goa/homes?place_id=ChIJQbc2YxC6vzsRkkDzYv-H-Oo&refinement_paths%5B%5D=%2Fhomes'

type City = {
  city_slug: string
  city_name: string
  search_url?: string
  listing_count: number
  scraped_count: number
  enriched_count: number
  job_state?: string
  current_step?: string
  last_batch_uuid?: string
  csv_storage_path?: string
  enrich_thread_alive?: boolean
}

type CityListing = {
  listing_id: string
  listing_url: string
  title?: string
  thumbnail?: string
  location_text?: string
  price_text?: string
  rating?: string
  host_name?: string
  scrape_status: string
  enrichment_status?: string
  phone?: string
  email?: string
  instagram_url?: string
  enrichment_notes?: string
  updated_at?: string
  detail?: Record<string, unknown>
  contacts?: {
    phones?: string[]
    emails?: string[]
    instagrams?: string[]
  }
}

type ScrapeBatch = {
  batch_uuid: string
  status: string
  job_state?: string
  current_step?: string
  scraped_count: number
  row_count: number
  job_thread_alive?: boolean
}

function statusChip(status: string) {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    done: 'success',
    completed: 'success',
    skipped: 'info',
    running: 'warning',
    scraping: 'warning',
    failed: 'error',
    partial: 'warning',
    none: 'default',
    pending: 'default',
  }
  return <Chip label={status} size="small" color={map[status] || 'default'} sx={{ height: 22, fontSize: 11 }} />
}

function contactPreview(raw?: string, extras?: string[]) {
  const items = (extras?.length ? extras : String(raw || '').split(/[,;]+/).map((s) => s.trim()).filter(Boolean))
  if (!items.length) return '—'
  const head = items.slice(0, 2).join(', ')
  return items.length > 2 ? `${head} +${items.length - 2}` : head
}

export default function AirbnbScrapePage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { setJobRunning } = useAdminJob()

  const [cities, setCities] = useState<City[]>([])
  const [citiesLoading, setCitiesLoading] = useState(true)
  const [selectedCity, setSelectedCity] = useState<string>('goa')
  const [cityMeta, setCityMeta] = useState<City | null>(null)
  const [listings, setListings] = useState<CityListing[]>([])
  const [listingsLoading, setListingsLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [detailId, setDetailId] = useState<string | null>(null)
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const [searchUrl, setSearchUrl] = useState(GOA_EXAMPLE)
  const [cityName, setCityName] = useState('Goa')
  const [maxListings, setMaxListings] = useState(15)
  const [batchUuid, setBatchUuid] = useState('')
  const [scrapeJob, setScrapeJob] = useState<ScrapeBatch | null>(null)
  const [logs, setLogs] = useState<{ id: number; level: string; message: string; created_at: string }[]>([])
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null)
  const logEndRef = useRef<HTMLDivElement>(null)

  const isScraping = Boolean(scrapeJob?.job_thread_alive)
  const isEnriching = Boolean(cityMeta?.enrich_thread_alive)
  const isRunning = isScraping || isEnriching

  useEffect(() => {
    setJobRunning(!!isRunning)
  }, [isRunning, setJobRunning])

  const loadCities = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setCitiesLoading(true)
    try {
      const j = await fetchScrapeCities(50)
      const list = (j.cities || []) as City[]
      setCities(list)
      setSelectedCity((prev) => {
        if (list.length && !list.find((c) => c.city_slug === prev)) {
          return list[0].city_slug
        }
        return prev
      })
    } catch {
      if (!opts?.silent) setCities([])
    } finally {
      setCitiesLoading(false)
    }
  }, [])

  const cityLoadGen = useRef(0)

  const refreshCity = useCallback(async (opts?: { silent?: boolean }) => {
    if (!selectedCity) return
    const gen = ++cityLoadGen.current
    if (!opts?.silent) {
      setListingsLoading(true)
      setCityMeta(null)
      setListings([])
    }
    try {
      const [cityRes, listRes] = await Promise.all([
        fetchScrapeCity(selectedCity),
        fetchCityListings(selectedCity, 500),
      ])
      if (gen !== cityLoadGen.current) return
      setCityMeta(cityRes.city as City)
      setListings((listRes.listings || []) as CityListing[])

      const batchId = (cityRes.city as City)?.last_batch_uuid
      if (batchId) {
        setBatchUuid(batchId)
        const [st, lg] = await Promise.all([fetchAirbnbStatus(batchId), fetchAirbnbLogs(batchId)])
        if (gen !== cityLoadGen.current) return
        setScrapeJob(st.batch as ScrapeBatch)
        setLogs(lg.logs || [])
      }
    } catch {
      if (gen !== cityLoadGen.current) return
      if (!opts?.silent) {
        setCityMeta(null)
        setListings([])
      }
    } finally {
      if (gen === cityLoadGen.current && !opts?.silent) setListingsLoading(false)
    }
  }, [selectedCity])

  useEffect(() => {
    loadCities().catch(() => {})
  }, [loadCities])

  const citySlugs = useMemo(() => cities.map((c) => c.city_slug).join(','), [cities])

  useEffect(() => {
    if (citiesLoading) return
    if (!citySlugs || !citySlugs.split(',').includes(selectedCity)) {
      setListingsLoading(false)
      setListings([])
      setCityMeta(null)
      return
    }
    refreshCity().catch(() => {})
  }, [refreshCity, citiesLoading, citySlugs, selectedCity])

  useEffect(() => {
    if (!isRunning) return
    const t = window.setInterval(() => {
      loadCities({ silent: true }).catch(() => {})
      refreshCity({ silent: true }).catch(() => {})
    }, 3000)
    return () => window.clearInterval(t)
  }, [isRunning, loadCities, refreshCity])

  useEffect(() => {
    if (!detailId || !selectedCity) {
      setDetail(null)
      return
    }
    setDetailLoading(true)
    fetchCityListingDetail(selectedCity, detailId)
      .then((j) => setDetail(j.listing as Record<string, unknown>))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false))
  }, [detailId, selectedCity])

  const selectCity = (slug: string) => {
    const c = cities.find((x) => x.city_slug === slug)
    setSelectedCity(slug)
    setSelectedIds(new Set())
    setDetailId(null)
    if (c?.search_url) setSearchUrl(c.search_url)
    if (c?.city_name) setCityName(c.city_name)
  }

  const toggleAll = () => {
    if (selectedIds.size === listings.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(listings.map((l) => l.listing_id)))
    }
  }

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleStart = async () => {
    setBusy(true)
    setFlash(null)
    try {
      const json = await startAirbnbScrape({
        search_url: searchUrl.trim(),
        place_label: cityName.trim(),
        max_listings: maxListings,
      })
      setBatchUuid(json.batch_uuid)
      setScrapeJob({
        batch_uuid: json.batch_uuid,
        status: 'scraping',
        job_state: 'running',
        job_thread_alive: true,
        scraped_count: 0,
        row_count: 0,
      })
      setFlash({ type: 'success', text: `Scrape started for ${cityName} — skipping already-scraped listings` })
      await loadCities({ silent: true })
      if (json.city_slug && json.city_slug !== selectedCity) {
        setSelectedCity(json.city_slug)
      } else {
        await refreshCity({ silent: true })
      }
    } catch (e) {
      setFlash({ type: 'error', text: e instanceof Error ? e.message : 'Failed to start scrape' })
    } finally {
      setBusy(false)
    }
  }

  const handleEnrich = async () => {
    if (!selectedIds.size) return
    setBusy(true)
    setFlash(null)
    try {
      await enrichCityListings(selectedCity, Array.from(selectedIds))
      setFlash({ type: 'info', text: `Enriching ${selectedIds.size} listing(s) via Napps AI…` })
      await refreshCity({ silent: true })
    } catch (e) {
      setFlash({ type: 'error', text: e instanceof Error ? e.message : 'Enrichment failed' })
    } finally {
      setBusy(false)
    }
  }

  const scrapeProgress = useMemo(() => {
    if (!scrapeJob?.row_count) return 0
    return Math.min(100, (scrapeJob.scraped_count / Math.max(scrapeJob.row_count, maxListings)) * 100)
  }, [scrapeJob, maxListings])

  return (
    <PageShell
      title="City Scraper"
      subtitle="One CSV per city · skip duplicates · select & enrich"
      icon={<LocationCityRoundedIcon sx={{ color: '#C9A227' }} />}
    >
      {flash && (
        <Alert severity={flash.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setFlash(null)}>
          {flash.text}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {/* City sidebar */}
        <Paper
          className="admin-panel admin-city-sidebar"
          elevation={0}
          sx={{
            width: { xs: '100%', md: 220 },
            flexShrink: 0,
            maxHeight: { md: 'calc(100dvh - 140px)' },
            overflow: 'auto',
          }}
        >
          <Box className="admin-panel-header" sx={{ py: 1.25, px: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Cities
            </Typography>
            {citiesLoading && <CircularProgress size={14} />}
          </Box>
          <Stack
            direction={{ xs: 'row', md: 'column' }}
            spacing={0.75}
            sx={{ p: 1, overflowX: { xs: 'auto', md: 'visible' } }}
          >
            {citiesLoading &&
              [0, 1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  className="admin-city-skel"
                  variant="rounded"
                  height={56}
                  animation="wave"
                  sx={{ minWidth: { xs: 140, md: '100%' }, flexShrink: 0 }}
                />
              ))}
            {!citiesLoading && cities.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
                No cities yet — start a scrape
              </Typography>
            )}
            {!citiesLoading &&
              cities.map((c) => (
              <Button
                key={c.city_slug}
                size="small"
                variant={selectedCity === c.city_slug ? 'contained' : 'text'}
                onClick={() => selectCity(c.city_slug)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  borderRadius: 2,
                  py: 1,
                  px: 1.5,
                  minWidth: { xs: 140, md: '100%' },
                  flexShrink: 0,
                }}
              >
                <Stack alignItems="flex-start" spacing={0.25}>
                  <Typography variant="body2" fontWeight={600}>
                    {c.city_name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    {c.listing_count} listings · {c.enriched_count} enriched
                  </Typography>
                </Stack>
              </Button>
            ))}
          </Stack>
        </Paper>

        {/* Main workspace */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <StatCard label="Listings" value={listingsLoading && !cityMeta ? '…' : (cityMeta?.listing_count ?? 0)} />
            <StatCard label="Enriched" value={listingsLoading && !cityMeta ? '…' : (cityMeta?.enriched_count ?? 0)} />
            <StatCard label="City CSV" value={listingsLoading && !cityMeta ? '…' : cityMeta?.csv_storage_path ? 'Ready' : '—'} />
          </Stack>

          <Paper className="admin-panel" elevation={0} sx={{ mb: 2 }}>
            <Box className="admin-panel-header">
              <Typography variant="h6" fontWeight={700}>
                Scrape {cityName || 'city'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Max N = N new listings. Already-scraped rooms are skipped and the next page is used.
              </Typography>
            </Box>
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <TextField
                fullWidth
                size="small"
                label="City name"
                value={cityName}
                onChange={(e) => setCityName(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Airbnb search URL"
                value={searchUrl}
                onChange={(e) => setSearchUrl(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                <TextField
                  type="number"
                  size="small"
                  label="Max new listings"
                  value={maxListings}
                  onChange={(e) => setMaxListings(Math.min(60, Math.max(1, Number(e.target.value) || 15)))}
                  sx={{ width: { xs: '100%', sm: 140 } }}
                />
                <Button
                  variant="contained"
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowRoundedIcon />}
                  disabled={!searchUrl.trim() || !cityName.trim() || busy || isScraping}
                  onClick={handleStart}
                >
                  Scrape new
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<StopRoundedIcon />}
                  disabled={!isScraping || !batchUuid}
                  onClick={async () => {
                    if (batchUuid) await stopAirbnbScrape(batchUuid)
                    refreshCity({ silent: true })
                  }}
                >
                  Stop
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadRoundedIcon />}
                  disabled={!cityMeta?.csv_storage_path}
                  onClick={() => downloadCityCsv(selectedCity)}
                >
                  CSV
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AutoFixHighRoundedIcon />}
                  disabled={!selectedIds.size || busy || isEnriching}
                  onClick={handleEnrich}
                >
                  Enrich ({selectedIds.size})
                </Button>
                <IconButton onClick={() => refreshCity()} aria-label="refresh">
                  <RefreshRoundedIcon />
                </IconButton>
              </Stack>

              {isScraping && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" display="block" mb={0.5}>
                    {scrapeJob?.current_step || 'Scraping…'}
                  </Typography>
                  <LinearProgress variant="determinate" value={scrapeProgress} sx={{ height: 6, borderRadius: 99 }} />
                </Box>
              )}
              {isEnriching && (
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">{cityMeta?.current_step || 'Enriching…'}</Typography>
                  </Stack>
                </Box>
              )}
            </Box>
          </Paper>

          <Paper className="admin-panel admin-city-table-panel" elevation={0}>
            <Box className="admin-panel-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" fontWeight={700}>
                  {cityMeta?.city_name || selectedCity} listings
                </Typography>
                {listingsLoading && <CircularProgress size={16} />}
              </Stack>
              <Chip label={listingsLoading ? 'Loading…' : `${listings.length} rows`} size="small" />
            </Box>
            {listingsLoading && <LinearProgress sx={{ height: 3 }} />}
            <TableContainer className="admin-table-scroll">
              <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: '#faf8f5' }}>
                      <Checkbox
                        size="small"
                        indeterminate={selectedIds.size > 0 && selectedIds.size < listings.length}
                        checked={listings.length > 0 && selectedIds.size === listings.length}
                        onChange={toggleAll}
                      />
                    </TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5' }}>Thumb</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5', minWidth: 160 }}>Title</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5' }}>Location</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5' }}>Price</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5' }}>Rating</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5' }}>Scrape</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5' }}>Enrich</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5' }}>Mobile</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5' }}>Email</TableCell>
                    <TableCell sx={{ bgcolor: '#faf8f5' }}>Instagram</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listingsLoading &&
                    [0, 1, 2, 3, 4, 5].map((i) => (
                      <TableRow key={`skel-${i}`}>
                        <TableCell padding="checkbox">
                          <Skeleton variant="circular" width={18} height={18} />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="rounded" width={44} height={44} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width="80%" />
                        </TableCell>
                        <TableCell>
                          <Skeleton width="70%" />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={64} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={48} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={56} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={56} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={72} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={90} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                      </TableRow>
                    ))}
                  {!listingsLoading && listings.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 6, opacity: 0.6 }}>
                        No listings for this city yet
                      </TableCell>
                    </TableRow>
                  )}
                  {!listingsLoading &&
                    listings.map((row) => (
                    <TableRow
                      key={row.listing_id}
                      hover
                      selected={selectedIds.has(row.listing_id)}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setDetailId(row.listing_id)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox size="small" checked={selectedIds.has(row.listing_id)} onChange={() => toggleOne(row.listing_id)} />
                      </TableCell>
                      <TableCell>
                        {row.thumbnail ? (
                          <Box
                            component="img"
                            src={row.thumbnail}
                            alt=""
                            sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1 }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }}>
                          {row.title || row.listing_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 160, display: 'block' }}>
                          {row.location_text || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.price_text || '—'}</TableCell>
                      <TableCell>{row.rating ? `★ ${row.rating}` : '—'}</TableCell>
                      <TableCell>{statusChip(row.scrape_status)}</TableCell>
                      <TableCell>{statusChip(row.enrichment_status || 'none')}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', display: 'block', maxWidth: 180 }}>
                          {contactPreview(row.phone, row.contacts?.phones)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 160, display: 'block' }}>
                          {contactPreview(row.email, row.contacts?.emails)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 140, display: 'block' }}>
                          {contactPreview(row.instagram_url, row.contacts?.instagrams)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {batchUuid && (
            <Paper className="admin-panel" elevation={0} sx={{ mt: 2 }}>
              <Box className="admin-panel-header">
                <Typography variant="subtitle2" fontWeight={700}>
                  Job logs
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <TerminalLogs title="scraper" logs={logs} logEndRef={logEndRef} />
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      <ListingDetailDrawer
        open={!!detailId}
        isMobile={isMobile}
        loading={detailLoading}
        listingId={detailId}
        detail={detail}
        onClose={() => setDetailId(null)}
      />

      {isMobile && (
        <Box className="admin-mobile-bar admin-mobile-bar--dual">
          <Button
            fullWidth
            variant="outlined"
            disabled={!selectedIds.size || isEnriching}
            onClick={handleEnrich}
            startIcon={<AutoFixHighRoundedIcon />}
          >
            Enrich ({selectedIds.size})
          </Button>
          <Button fullWidth variant="contained" disabled={isScraping || busy} onClick={handleStart}>
            Scrape
          </Button>
        </Box>
      )}
    </PageShell>
  )
}
